import Clarity from '@microsoft/clarity';

const CONSENT_KEY = 'jdms.analyticsConsent';
const ATTRIBUTION_KEY = 'jdms.attribution';
const ATTRIBUTION_PARAMETERS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'msclkid', 'fbclid', 'li_fat_id'];
const journey = document.body.dataset.journey || 'general';
const campaignId = document.body.dataset.campaign || __CAMPAIGN_ID__;
const siteId = __SITE_ID__;
let clarityStarted = false;

window.dataLayer = window.dataLayer || [];
function gtag() {
  window.dataLayer.push(arguments);
}

function loadGtm() {
  if (!__GTM_CONTAINER_ID__ || document.querySelector('script[data-jdms-gtm]')) return;
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.dataset.jdmsGtm = 'true';
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(__GTM_CONTAINER_ID__)}`;
  document.head.appendChild(script);
}

function startClarity() {
  if (!__CLARITY_PROJECT_ID__ || clarityStarted) return;
  Clarity.init(__CLARITY_PROJECT_ID__);
  Clarity.setTag('campaign', campaignId);
  Clarity.setTag('site', siteId);
  Clarity.setTag('journey', journey);
  Clarity.event('journey_viewed');
  clarityStarted = true;
}

function setConsent(granted) {
  const value = granted ? 'granted' : 'denied';
  localStorage.setItem(CONSENT_KEY, value);
  gtag('consent', 'update', {
    analytics_storage: value,
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
  });
  if (granted) startClarity();
  if (clarityStarted) {
    Clarity.consentV2({ analytics_Storage: value, ad_Storage: 'denied' });
  }
  document.getElementById('analytics-consent')?.remove();
}

function showConsentBanner() {
  if (document.getElementById('analytics-consent')) return;
  const banner = document.createElement('aside');
  banner.id = 'analytics-consent';
  banner.setAttribute('aria-label', 'Analytics consent');
  banner.innerHTML = `
    <p>We use privacy-focused analytics to improve this site. No form values are sent to analytics. <a href="https://hrizons.com/privacy-notice/" target="_blank" rel="noopener">Privacy notice</a></p>
    <div><button type="button" data-consent="denied">Decline</button><button type="button" data-consent="granted">Allow analytics</button></div>`;
  const style = document.createElement('style');
  style.textContent = '#analytics-consent{position:fixed;z-index:100000;right:1rem;bottom:1rem;max-width:36rem;padding:1rem;background:#fff;color:#17232b;border:1px solid #d8dfe3;border-radius:.5rem;box-shadow:0 8px 30px rgba(0,0,0,.2);font:14px/1.45 Arial,sans-serif}#analytics-consent p{margin:0 0 .75rem}#analytics-consent div{display:flex;justify-content:flex-end;gap:.5rem}#analytics-consent button{padding:.55rem .8rem;border:1px solid #17232b;border-radius:.25rem;background:#fff;color:#17232b;cursor:pointer}#analytics-consent button[data-consent="granted"]{background:#17232b;color:#fff}';
  banner.appendChild(style);
  banner.addEventListener('click', (event) => {
    const choice = event.target.closest('[data-consent]')?.dataset.consent;
    if (choice) setConsent(choice === 'granted');
  });
  document.body.appendChild(banner);
}

function safeValue(value) {
  const normalized = String(value ?? '').slice(0, 100);
  return /@|%40/i.test(normalized) ? '[redacted]' : normalized;
}

function readAttribution() {
  const current = Object.fromEntries(
    ATTRIBUTION_PARAMETERS.flatMap((name) => {
      const value = new URLSearchParams(location.search).get(name);
      return value ? [[name, safeValue(value)]] : [];
    })
  );
  let stored = {};
  try {
    stored = JSON.parse(localStorage.getItem(ATTRIBUTION_KEY) || '{}');
  } catch {
    localStorage.removeItem(ATTRIBUTION_KEY);
  }
  if (Object.keys(current).length) {
    stored = { first: stored.first || current, latest: current };
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(stored));
  }
  return stored;
}

const attribution = readAttribution();
function track(eventName, parameters = {}) {
  const event = {
    event: eventName,
    campaign_id: campaignId,
    site: siteId,
    journey,
    ...Object.fromEntries(Object.entries(parameters).map(([key, value]) => [key, safeValue(value)]))
  };
  window.dataLayer.push(event);
  if (clarityStarted && eventName !== 'journey_viewed') Clarity.event(eventName);
}

function initTracking() {
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500
  });
  loadGtm();

  const legalLinks = document.querySelector('.site-footer__legal-links');
  if (legalLinks && !legalLinks.querySelector('[data-open-consent]')) {
    const privacyChoices = document.createElement('a');
    privacyChoices.href = '#analytics-consent';
    privacyChoices.dataset.openConsent = 'true';
    privacyChoices.textContent = 'Privacy choices';
    privacyChoices.addEventListener('click', (event) => {
      event.preventDefault();
      showConsentBanner();
    });
    legalLinks.appendChild(privacyChoices);
  }

  const storedConsent = localStorage.getItem(CONSENT_KEY);
  if (!__REQUIRE_ANALYTICS_CONSENT__ || storedConsent === 'granted') setConsent(true);
  else if (storedConsent !== 'denied') showConsentBanner();

  track('journey_viewed', {
    page_path: location.pathname,
    utm_source: attribution.latest?.utm_source || attribution.first?.utm_source || '',
    utm_medium: attribution.latest?.utm_medium || attribution.first?.utm_medium || '',
    utm_campaign: attribution.latest?.utm_campaign || attribution.first?.utm_campaign || ''
  });

  document.addEventListener('click', (event) => {
    const target = event.target.closest('a,button');
    if (!target) return;
    const href = target instanceof HTMLAnchorElement ? target.href : '';
    const destination = href ? new URL(href, location.href) : null;
    const name = target.dataset.ctaName || target.dataset.resourceName || destination?.pathname || target.type || 'unknown';

    if (target.matches('.journey-card')) track('journey_selected', { destination: destination?.pathname || '' });
    if (target.matches('.btn,[data-cta-name]')) track('cta_clicked', { cta_name: name });
    if (target.dataset.resourceName || /\/resources\//.test(destination?.pathname || '')) {
      track('resource_clicked', { resource_name: name });
    }
    if (destination && destination.origin !== location.origin && !href.startsWith('mailto:') && !href.startsWith('tel:')) {
      track('outbound_clicked', { destination_host: destination.hostname });
    }
  });

  document.addEventListener('campaign:track', (event) => {
    const { eventName, parameters } = event.detail || {};
    if (eventName) track(eventName, parameters);
  });

  const observedDepths = new Set();
  let scheduled = false;
  addEventListener('scroll', () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      const scrollable = document.documentElement.scrollHeight - innerHeight;
      if (scrollable <= 0) return;
      const percent = Math.round((scrollY / scrollable) * 100);
      for (const depth of [25, 50, 75, 90]) {
        if (percent >= depth && !observedDepths.has(depth)) {
          observedDepths.add(depth);
          track('scroll_depth', { percent_scrolled: depth });
        }
      }
    });
  }, { passive: true });

  document.querySelectorAll('video').forEach((video) => {
    video.addEventListener('play', () => track('video_started', { video_id: video.id || 'page-video' }), { once: true });
    const milestones = new Set();
    video.addEventListener('timeupdate', () => {
      if (!video.duration) return;
      const percent = Math.floor((video.currentTime / video.duration) * 100);
      for (const milestone of [25, 50, 75, 100]) {
        if (percent >= milestone && !milestones.has(milestone)) {
          milestones.add(milestone);
          track('video_progress', { video_id: video.id || 'page-video', percent_complete: milestone });
        }
      }
    });
  });
}

window.JDMSAnalytics = { track, setConsent };
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initTracking);
else initTracking();
